/**
 * Marketing hub — central entry point for admins to manage the two
 * public-facing marketing surfaces:
 *
 *   1. Campaigns     — time-bound, bundle-scoped promotional flows.
 *   2. Landing Pages — persistent generic entry points for the
 *                      marketing site (no bundle whitelist).
 *
 * The two collections are intentionally NOT merged at the data layer.
 * This is a UI/navigation consolidation only — Campaigns still hits
 * `/api/admin/campaigns` and Landing Pages still hits
 * `/api/admin/landing-pages`.
 *
 * Tab selection is mirrored to `?tab=campaigns|landing_pages` so deep
 * links from other admin surfaces continue to work.
 */
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Megaphone, Rocket, BarChart3 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Header } from '../../components/Header';
import CampaignsPanel from '../../components/CampaignsPanel';
import LandingPagesPanel from '../../components/LandingPagesPanel';
import CustomerAcquisitionPage from './CustomerAcquisitionPage';

const VALID_TABS = ['campaigns', 'landing_pages', 'acquisition'];
const DEFAULT_TAB = 'campaigns';

export default function CampaignsHubPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const requested = (searchParams.get('tab') || '').toLowerCase();
    const activeTab = VALID_TABS.includes(requested) ? requested : DEFAULT_TAB;

    // Normalise the URL if it arrived with a missing/invalid ?tab param
    // so refreshes and shares always land on the same view.
    useEffect(() => {
        if (requested !== activeTab) {
            const next = new URLSearchParams(searchParams);
            next.set('tab', activeTab);
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const handleChange = (value) => {
        const next = new URLSearchParams(searchParams);
        next.set('tab', value);
        setSearchParams(next);
    };

    return (
        <div className="min-h-screen bg-background" data-testid="campaigns-hub-page">
            <Header />
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-6">
                    <h1 className="text-3xl font-semibold">Marketing</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Manage promotional campaigns and marketing-site landing pages.
                    </p>
                </div>

                <Tabs value={activeTab} onValueChange={handleChange} className="w-full">
                    <TabsList data-testid="marketing-hub-tabs">
                        <TabsTrigger
                            value="campaigns"
                            className="flex items-center gap-2"
                            data-testid="tab-marketing-campaigns"
                        >
                            <Megaphone className="h-4 w-4" />
                            Campaigns
                        </TabsTrigger>
                        <TabsTrigger
                            value="landing_pages"
                            className="flex items-center gap-2"
                            data-testid="tab-marketing-landing-pages"
                        >
                            <Rocket className="h-4 w-4" />
                            Landing Pages
                        </TabsTrigger>
                        <TabsTrigger
                            value="acquisition"
                            className="flex items-center gap-2"
                            data-testid="tab-marketing-acquisition"
                        >
                            <BarChart3 className="h-4 w-4" />
                            Acquisition
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="campaigns" className="mt-6" data-testid="tab-content-campaigns">
                        <CampaignsPanel />
                    </TabsContent>

                    <TabsContent value="landing_pages" className="mt-6" data-testid="tab-content-landing-pages">
                        <LandingPagesPanel />
                    </TabsContent>

                    <TabsContent value="acquisition" className="mt-6" data-testid="tab-content-acquisition">
                        <CustomerAcquisitionPage embedded />
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
}
